/**
 * Simple Angular controller that is attached to the GridVendors within the index.aspx. The purpose of this is to allow the user to easily
 * deactivate vendors. This is important for a lot of our users due to duplicate vendors being created. Now whenever there are a lot of
 * duplicate vendors from an import they can just easily multi-select the ones they want to deactivate instead of having to find it individually
 * and deactivate them.
 */
/* @ngInject */ export function bulkVendorsController(
  $scope,
  Alert,
  BooksSvc,
  BusyScreen,
  uiGridConstants,
  VendorsService
) {
  const alertTitle = 'Deactivate Vendors';

  const COLUMNS = [
    {
      field: 'fullName',
      displayName: 'Vendor Name',
      toolTip: true,
      width: 250
    },
    { field: 'email', toolTip: true },
    { field: 'phone' },
    { field: 'taxID', displayName: 'Tax ID' },
    {
      field: 'funds',
      cellTemplate:
        '<div class="ui-grid-cell-contents">${{COL_FIELD.toFixed(2)}}</div>'
    },
    {
      field: 'taxPercent',
      cellTemplate:
        '<div class="ui-grid-cell-contents">{{(COL_FIELD * 100).toFixed(2)}}%</div>'
    },
    { field: 'notes', toolTip: true },
    {
      field: 'f1099',
      maxWidth: 80,
      cellTemplate:
        '<div class="ui-grid-cell-contents">{{COL_FIELD ? "Yes" : "No"}}</div>',
      filter: {
        type: uiGridConstants.filter.SELECT,
        selectOptions: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' }
        ]
      }
    }
  ];

  const gridOptions = {
    data: 'dbVendors',
    enableColumnMenus: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableFiltering: true,
    enableRowSelection: true,
    enableSelectAll: true,
    minRowsToShow: 3,
    multiSelect: true,
    enableFullRowSelection: true,
    enableColumnResizing: true,
    showGridFooter: true,
    /**
     * We register the grid api to the $scope so we have reference to which rows are clicked and that way we can interact with them in this file.
     */
    onRegisterApi: function (gridApi) {
      $scope.gridApi = gridApi;

      gridApi.selection.on.rowSelectionChanged($scope, function (row) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifySingleSelect(selectedRows.length, row, alertTitle, 1000);
      });
      gridApi.selection.on.rowSelectionChangedBatch($scope, function (rows) {
        const selectedRows = gridApi.selection.getSelectedRows();

        $scope.verifyMultiSelect(selectedRows.length, rows, alertTitle, 1000);
      });
    },
    columnDefs: COLUMNS
  };

  /**
   * Bind the grip options we set to the $scope so we can use them in the index file.
   */
  $scope.vendorOptions = gridOptions;

  /**
   * Finds all the selected rows in the grid and maps them to their ID's. This is used to pass to the server to deactivate the vendors by their ID's.
   *
   * @returns void
   */
  function deactivateVendors() {
    const selectedRows = $scope.gridApi.selection.getSelectedRows();

    if (!selectedRows.length) {
      Alert.info(
        'No Purchase Orders have been selected. Please select one or more before continuing.',
        alertTitle
      );
      return;
    }

    const vendorsToDeactivate = selectedRows.map((vendor) => vendor.id);

    BusyScreen.setBusy(true);

    VendorsService.bulkDeactivateVendors(vendorsToDeactivate)
      .then(() => BooksSvc.getVendorList(true, true))
      .then((resp) => {
        // Refresh the vendors now that vendors have been deactivated
        $scope.dbVendors = resp.data.sortOn('fullName');
        BusyScreen.setBusy(false);
        Alert.success(
          'The selected vendor(s) have been deactivated.',
          alertTitle
        );
      })
      .catch((error) => {
        BusyScreen.setBusy(false);

        const errorMesssage =
          error.parsedResponse && error.parsedResponse.message()
            ? error.parsedResponse.message()
            : 'There was an error deactivating those vendor(s). Please try again.';

        Alert.error(errorMesssage, alertTitle);
      });
  }

  /**
   * Simple function that checks to see if there are any selected rows. If there are then we can save, if not then we cannot save.
   * If there are no selected rows then the user has not selected anything to save.
   *
   * @returns boolean (true = can Save; false = cannot save)
   */
  function canSaveVendors() {
    return $scope.gridApi.selection.getSelectedRows().length > 0;
  }

  /**
   * Attach these to the $scope so they can be used in the index file.
   */
  $scope.deactivateVendors = deactivateVendors;
  $scope.canSaveVendors = canSaveVendors;
}
